import {
	IEditEventBookmark,
	IGetAIEventsRequestData,
	IGetAIEventsResponse,
	IGetAlertEventsRequestData,
	IGetAlertEventsResponse,
	IGetAlertEventsResponseData,
	IGetEnforcementSessionsRequestData,
	IGetEnforcementSessionsResponse,
	IGetImageURLRequestData,
	IGetImageURLResponseData,
	IGetVehicleDetailsRequestData,
	IPreviewChallanDetails,
	ICreateChallanRequestData,
	ICreateChallanResponse,
	IUpdateEvidenceImageRequestData,
	IUpdateVehicleEventRequestData,
	IValidateViolationsRequestData,
	IVehicleDetails,
	IUploadPlateImageRequestData
} from "../../types";
import { getAPIHeaderOptions, transformErrorResponse } from "../../utils/apiUtils";
import { queries } from "./queries.reducer";

const alerts = queries.injectEndpoints({
	endpoints: (builder) => ({
		// GET ALERT EVENTS
		getAlertEvents: builder.query<IGetAlertEventsResponse, IGetAlertEventsRequestData>({
			query: (requestData) => ({
				url: "/analytics/event",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// VALIDATE VIOLATIONS
		validateViolations: builder.mutation<unknown, IValidateViolationsRequestData>({
			query: (requestData) => ({
				url: "/analytics/number-plate-violations",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			})
		}),

		// EDIT EVENT BOOKMARK
		editEventBookmark: builder.mutation<unknown, IEditEventBookmark>({
			query: (requestData) => ({
				url: `/analytics/event/${requestData.event_id}`,
				headers: getAPIHeaderOptions(true),
				method: "PATCH",
				body: requestData.data
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// GET MEDIA URL
		getMediaURL: builder.query<IGetImageURLResponseData, IGetImageURLRequestData>({
			query: (requestData) => ({
				url: `/analytics/serve-file`,
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData,
				responseHandler: (response) => {
					return new Promise((resolve) => {
						if (response?.headers?.get("Content-Type") !== "application/json") {
							resolve({ url: response.url });
							return;
						}

						response
							.json()
							.then((responseData) => resolve(responseData))
							.catch(() => resolve({ url: response.url }));
					});
				}
			}),
			transformResponse: (response: IGetImageURLResponseData) => {
				if (response.url === "NOT_FOUND") {
					return { url: "" };
				}

				return response;
			}
		}),

		// GET AI EVENTS
		getAIEvents: builder.query<IGetAIEventsResponse, IGetAIEventsRequestData>({
			query: (requestData) => ({
				url: "/ai-search/search",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// GET ENFORCEMENT SESSIONS
		getEnforcementSessions: builder.query<IGetEnforcementSessionsResponse, IGetEnforcementSessionsRequestData>({
			query: (requestData) => ({
				url: "/analytics/session",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// UPDATE VEHICLE EVENT
		updateVehicleEvent: builder.mutation<IGetAlertEventsResponseData, IUpdateVehicleEventRequestData>({
			query: (requestData) => ({
				url: `/analytics/event/${requestData.unique_id}`,
				headers: getAPIHeaderOptions(true),
				method: "PATCH",
				body: requestData.data
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// UPDATE EVIDENCE IMAGE
		updateEvidenceImage: builder.mutation<unknown, IUpdateEvidenceImageRequestData>({
			query: (requestData) => ({
				url: `/analytics/evidence/${requestData.id}`,
				headers: getAPIHeaderOptions(true),
				method: "PATCH",
				body: requestData.data
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// GET VEHICLE DETAILS
		getVehicleDetails: builder.query<IVehicleDetails, IGetVehicleDetailsRequestData>({
			query: (requestData) => ({
				url: "/analytics/vehicle-details",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// PREVIEW CHALLAN DETAILS
		previewChallanDetails: builder.query<IPreviewChallanDetails, number>({
			query: (eventId) => ({
				url: `/analytics/challans/preview/${eventId}/`,
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// CREATE CHALLANS
		createChallans: builder.mutation<ICreateChallanResponse, ICreateChallanRequestData>({
			query: (requestData) => ({
				url: "/analytics/challans/create/",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// RETRY CHALLANS
		retryChallans: builder.mutation<ICreateChallanResponse, ICreateChallanRequestData>({
			query: (requestData) => ({
				url: "/analytics/challans/retry/",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// UPLOAD PLATE IMAGE
		uploadPlateImage: builder.mutation<unknown, IUploadPlateImageRequestData>({
			query: (requestData) => ({
				url: "/analytics/plate-evidence-upload",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse
		})
	})
});

export const {
	// GET ALERT EVENTS
	useGetAlertEventsQuery,
	// VALIDATE VIOLATIONS
	useValidateViolationsMutation,
	// EDIT EVENT BOOKMARK
	useEditEventBookmarkMutation,
	// GET IMAGE URL
	useGetMediaURLQuery,
	// GET AI EVENTS
	useGetAIEventsQuery,
	// GET ENFORCEMENT SESSIONS
	useGetEnforcementSessionsQuery,
	// UPDATE VEHICLE EVENT
	useUpdateVehicleEventMutation,
	// UPDATE EVIDENCE IMAGE
	useUpdateEvidenceImageMutation,
	// GET VEHICLE DETAILS
	useGetVehicleDetailsQuery,
	useLazyGetVehicleDetailsQuery,
	// PREVIEW CHALLAN DETAILS
	usePreviewChallanDetailsQuery,
	// CREATE CHALLANS
	useCreateChallansMutation,
	// RETRY CHALLANS
	useRetryChallansMutation,
	// UPLOAD PLATE IMAGE
	useUploadPlateImageMutation
} = alerts;
